import { useMemo } from 'react';
import {
  mapEquityInfoSerializerToLocalState,
  mapDocumentToSave,
} from '../utils';
import { SubmitHandler, useForm } from '@agrotoken-tech/ui';
import { VALIDATION_STATUSES } from '@bcba/types';
import { CompanyEquityInformationSerializer } from '@bcba/serializers';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { UpsertCompanyEquityInformationDto } from '../../../../services/company/dto';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
} from '@tanstack/react-query';
import { putEquityInfo } from '../../../../services/company';
import { useDigitalDossierActionButtons } from '../../hooks/useDigitalDossierActionButtons';
import { usePendingChangesAlert } from '../../../../contexts/hooks';

const resolver = classValidatorResolver(UpsertCompanyEquityInformationDto);

export const useEquityInfo = (
  defaultValues: CompanyEquityInformationSerializer,
  refetch: (
    options?: RefetchOptions | undefined,
  ) => Promise<QueryObserverResult<CompanyEquityInformationSerializer, Error>>,
) => {
  const { t } = useTranslation();
  const { showSuccessToast } = useToastsHook();

  const status = defaultValues.status;
  const validationMessage = defaultValues.validationMessage;
  const isPendingChangeRequest =
    status === VALIDATION_STATUSES.PENDING_CHANGE_REQUEST;

  const apiDataMappedToLocalState = useMemo(() => {
    return mapEquityInfoSerializerToLocalState(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(defaultValues)]);

  const formMethods = useForm({
    resolver,
    defaultValues: apiDataMappedToLocalState,
  });
  const {
    handleSubmit,
    formState: { isDirty },
    reset,
  } = formMethods;
  usePendingChangesAlert(isDirty);

  const handleSave: SubmitHandler<any> = (data) => {
    const { balanceDocuments, additionalDocuments } = data;
    const payload = {
      balanceDocuments: balanceDocuments?.map(mapDocumentToSave),
      additionalDocuments: additionalDocuments?.map(mapDocumentToSave),
    };

    saveChanges(payload);
  };

  const { mutate: saveChanges, isPending: saveIsPending } = useMutation({
    mutationFn: putEquityInfo,
    onSuccess: async (data: CompanyEquityInformationSerializer) => {
      showSuccessToast({
        title: t('explorer.equityInformation.toast.success.title'),
      });
      await refetch();

      const updatedData = mapEquityInfoSerializerToLocalState(data);
      reset(updatedData, { keepDirty: false });
    },
  });

  const { actionButtons, isFormReadOnly } = useDigitalDossierActionButtons({
    status,
    isLoadingSave: saveIsPending,
    formHasChanges: isDirty,
  });

  return {
    isPendingChangeRequest,
    validationMessage,
    handleSubmit,
    formMethods,
    handleSave,
    actionButtons,
    isFormReadOnly,
  };
};

export default useEquityInfo;
